import { OnEvent, Service } from '@toeverything/infra';
import { Array as YArray } from 'yjs';

import { dedupeDocMetas } from '../../doc/stores/doc-meta-repair';
import type { GuardService } from '../../permissions';
import { type Workspace, WorkspaceInitialized } from '../../workspace';

/**
 * Once per workspace load, collapses duplicated page-list entries (the same
 * doc id listed twice, which left an un-trashable copy in "All docs").
 *
 * It runs only after the root doc has synced with the server, so it repairs
 * the converged list rather than a partial local copy, and only for a user who
 * may write to the workspace.
 */
@OnEvent(WorkspaceInitialized, s => s.onWorkspaceInitialized)
export class DocMetaRepairService extends Service {
  private readonly abortController = new AbortController();

  constructor(private readonly guardService: GuardService) {
    super();
  }

  onWorkspaceInitialized(workspace: Workspace) {
    if (workspace.openOptions.isSharedMode) {
      return;
    }
    this.repairAfterSync(workspace).catch(error => {
      if (!this.abortController.signal.aborted) {
        console.error('[doc-meta-repair] failed', error);
      }
    });
  }

  private async repairAfterSync(workspace: Workspace) {
    const signal = this.abortController.signal;
    const docEngine = workspace.engine.doc;
    await docEngine.waitForDocReady(workspace.id, signal);
    await docEngine.waitForSynced(workspace.id, signal);
    // updates pulled from the server are applied to the root doc after sync
    await docEngine.waitForUpdated(workspace.id, signal);
    if (signal.aborted) return;
    if (!(await this.guardService.can('Workspace_CreateDoc'))) return;
    if (signal.aborted) return;

    const pages = workspace.rootYDoc.getMap('meta').get('pages');
    if (!(pages instanceof YArray)) return;
    const removed = dedupeDocMetas(pages);
    if (removed > 0) {
      console.info(
        `[doc-meta-repair] removed ${removed} duplicate page-list entries in workspace ${workspace.id}`
      );
    }
  }

  override dispose() {
    this.abortController.abort();
    super.dispose();
  }
}
